import React, { useState, useEffect, useCallback, createContext } from 'react';

import { useAxios } from '../hooks/useAxios';
import { IFeaturePermission, IHierarchyPermission, ILogoutData, IMasterDataPermissions, IUserAppSettings, IUserContext }
  from '../models/userContext.model';
import { IResponseStatus } from '../models/axios.model';

const UserContext = createContext<IUserContext>({
  isLoggedIn: false,
  userId: null,
  userFirstName: null,
  userName: null,
  email: null,
  isAdmin: null,
  employeeId: null,
  masterDataPermissions: null,
  hierarchyPermissions: null,
  featurePermissions: null,
  userSettings: null,
  token: null,
  expirationDate: null,
  logoutData: null,
  login: (userId: string, userFirstName: string, userName: string, email: string, isAdmin: boolean, employeeId: string,
    masterDataPermissions: IMasterDataPermissions, hierarchyPermissions: IHierarchyPermission[], featurePermissions: IFeaturePermission[],
    userSettings: IUserAppSettings, token: string, expirationDate?: Date) => {},
  logout: (logoutData: ILogoutData | null) => {},
  replacePersistentUserSettings: (userSettings: IUserAppSettings) => {},
  savePersistentUserSettings: async () => { return null; },
  setBusinessArea: (businessAreaId: string) => {},
  setOutlet: (outletId: string) => {},
  setDate: (date: Date) => {},
  setJobBucketsFilter: (jobBuckets: string[]) => {},
  setLogoutData: (logoutData: ILogoutData | null) => {},
  setPeriod: (periodId: string) => {},
  setPreshiftTotalsDisplays: (totalIds: string[]) => {},
  setPreshiftTotalDisplay: (totalId: string) => {},
  setSettlementTotalsDisplays: (totalIds: string[]) => {},
  setSettlementTotalDisplay: (totalId: string) => {},
  setTimesheetTotalsDisplays: (totalIds: string[]) => {},
  setTimesheetTotalDisplay: (totalId: string) => {},
  toggleDarkMode: (isDarkMode: boolean) => {},
  togglePresentationMode: (isPresentationMode) => {}
});
let logoutTimerId: number;
interface IProps {
  children: React.ReactNode;
};

export const UserContextProvider: React.FC<IProps> = props => {
  const [userId, setUserId] = useState<string | null>(null);
  const [userFirstName, setUserFirstName] = useState<string | null>(null);
  const [userName, setUserName] = useState<string | null>(null);
  const [email, setEmail] = useState<string | null>(null);
  const [isAdmin, setIsAdmin] = useState<boolean | null>(null);
  const [employeeId, setEmployeeId] = useState<string | null>(null);
  const [masterDataPermissions, setMasterDataPermissions] = useState<IMasterDataPermissions | null>(null);
  const [hierarchyPermissions, setHierarchyPermissions] = useState<IHierarchyPermission[]>([]);
  const [featurePermissions, setFeaturePermissions] = useState<IFeaturePermission[]>([]);
  const [userSettings, setUserSettings] = useState<IUserAppSettings | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [expirationDate, setExpirationDate] = useState<Date | null>(null);
  const [logoutData, setLogoutData] = useState<ILogoutData | null>(null);
  const { sendRequest } = useAxios();

  const loginHandler = useCallback((userId: string, userFirstName: string, userName: string, email: string, isAdmin: boolean, employeeId: string,
    masterDataPermissions: IMasterDataPermissions, hierarchyPermissions: IHierarchyPermission[], featurePermissions: IFeaturePermission[],
    userSettings: IUserAppSettings, token: string, expirationDate?: Date) => {
    setUserId(userId);
    setUserFirstName(userFirstName);
    setUserName(userName);
    setEmail(email);
    setIsAdmin(isAdmin);
    setEmployeeId(employeeId);
    setMasterDataPermissions(masterDataPermissions || null);
    setHierarchyPermissions(hierarchyPermissions || null);
    setFeaturePermissions(featurePermissions || null);
    setUserSettings(userSettings || null);
    setToken(token);
    const tokenExpirationDate = expirationDate ?? new Date(new Date().getTime() + 1000 * 3600 * 24);
    setExpirationDate(tokenExpirationDate);
    setLogoutData(null);
    localStorage.setItem(
      'userInfoRcbgDev',
      JSON.stringify({
        userId,
        userFirstName,
        userName,
        email,
        isAdmin,
        employeeId,
        masterDataPermissions,
        hierarchyPermissions,
        featurePermissions,
        userSettings,
        token,
        expirationDate: tokenExpirationDate.toISOString()
      }));
  }, []);
  
  const logoutHandler = useCallback((logoutData: ILogoutData | null) => {
    setUserId(null);
    setUserFirstName(null);
    setUserName(null);
    setEmail(null);
    setIsAdmin(null);
    setEmployeeId(null);
    setMasterDataPermissions(null);
    setHierarchyPermissions([]);
    setFeaturePermissions([]);
    setUserSettings(null);
    setToken(null);
    setExpirationDate(null);
    setLogoutData(logoutData);
    localStorage.removeItem('userInfoRcbgDev');
  }, []);

  const changeBusinessAreaHandler = useCallback((businessAreaId: string) => {
    setUserSettings(settings => {
      const updatedSettings = settings ? {...settings} : null;
      if (updatedSettings) {
        updatedSettings.businessAreaId = businessAreaId;
      }
      return updatedSettings;
    });
  }, []);

  const changeOutletHandler = useCallback((outletId: string) => {
    setUserSettings(settings => {
      const updatedSettings = settings ? {...settings} : null;
      if (updatedSettings) {
        updatedSettings.outletId = outletId;
      }
      return updatedSettings;
    });
  }, []);

  const changeDateHandler = useCallback((date: Date) => {
    setUserSettings(settings => {
      const updatedSettings = settings ? {...settings} : null;
      if (updatedSettings) {
        updatedSettings.date = date;
      }
      return updatedSettings;
    });
  }, []);

  const changeJobBucketsFilterHandler = useCallback((jobBuckets: string[]) => {
    setUserSettings(settings => {
      const updatedSettings = settings ? {...settings} : null;
      if (updatedSettings) {
        updatedSettings.jobBucketsFilter = jobBuckets;
      }
      return updatedSettings;
    });
  }, []);

  const changeLogoutDataHandler = useCallback((logoutData: ILogoutData | null) => {
    setLogoutData(logoutData);
  }, []);
  
  const changePeriodHandler = useCallback((periodId: string) => {
    setUserSettings(settings => {
      const updatedSettings = settings ? {...settings} : null;
      if (updatedSettings) {
        updatedSettings.periodId = periodId;
      }
      return updatedSettings;
    });    
  }, []);

  const toggleDarkModeHandler = useCallback(async (isDarkMode: boolean) => {
    setUserSettings(settings => {
      const updatedSettings = settings ? {...settings} : null;
      if (updatedSettings) {
        updatedSettings.isDarkMode = isDarkMode;
      }
      return updatedSettings;
    });

    const storedInfoString = localStorage.getItem('userInfoRcbgDev');
    const storedInfo = {
      ...storedInfoString ? JSON.parse(storedInfoString) : null
    };
    storedInfo.userSettings.isDarkMode = isDarkMode;
    localStorage.setItem('userInfoRcbgDev', JSON.stringify(storedInfo));

    if (token) {
      try {
        const callResponse = await sendRequest(
          '/users/darkMode',
          'PATCH',
          {
            isDarkMode
          },
          { Authorization: `Bearer ${token}` }
        );
        return callResponse;
      } catch (error) { }
    }
  }, [token, sendRequest]);

  const togglePresentationModeHandler = useCallback(async (isPresentationMode: boolean) => {
    setUserSettings(settings => {
      const updatedSettings = settings ? {...settings} : null;
      if (updatedSettings) {
        updatedSettings.isPresentationMode = isPresentationMode;
      }
      return updatedSettings;
    });

    const storedInfoString = localStorage.getItem('userInfoRcbgDev');
    const storedInfo = {
      ...storedInfoString ? JSON.parse(storedInfoString) : null
    };
    storedInfo.userSettings.isPresentationMode = isPresentationMode;
    localStorage.setItem('userInfoRcbgDev', JSON.stringify(storedInfo));

    if (token) {
      try {
        const callResponse = await sendRequest(
          '/users/presentationMode',
          'PATCH',
          {
            isPresentationMode
          },
          { Authorization: `Bearer ${token}` }
        );
        return callResponse;
      } catch (error) { }
    }
  }, [token, sendRequest]);

  const changePreshiftTotalsDisplaysHandler = useCallback((totalIds: string[]) => {
    setUserSettings(settings => {
      const updatedSettings = settings ? {...settings} : null;
      if (updatedSettings) {
        if (updatedSettings && updatedSettings?.preshiftTotalsDisplays?.length > 0) {
          const updatedDisplays = [...updatedSettings.preshiftTotalsDisplays.filter(x => totalIds.some(z => z === x.id))];
          const newDisplays = totalIds.filter(x => !updatedDisplays.some(z => z.id === x)).map(x => ({
            id: x,
            isExpanded: true
          }));
          if (newDisplays.length > 0) {
            updatedDisplays.push(...newDisplays);
          }
          updatedSettings.preshiftTotalsDisplays = updatedDisplays;
        } else {
          updatedSettings.preshiftTotalsDisplays = totalIds.map(x => ({
            id: x,
            isExpanded: true
          }));
        }
      }
      return updatedSettings;
    });
  }, []);

  const changePreshiftTotalDisplayHandler = useCallback((totalId: string) => {
    setUserSettings(settings => {
      const updatedSettings = settings ? {...settings} : null;
      if (updatedSettings) {
        const updatedDisplays = [...updatedSettings.preshiftTotalsDisplays];
        const index = updatedDisplays.findIndex(x => x.id === totalId);
        let item = {...updatedDisplays[index]};
        item.isExpanded = !item.isExpanded;
        updatedDisplays[index] = item;
        updatedSettings.preshiftTotalsDisplays = updatedDisplays;
      }
      return updatedSettings;
    });
  }, []);

  const changeSettlementTotalsDisplaysHandler = useCallback((totalIds: string[]) => {
    setUserSettings(settings => {
      const updatedSettings = settings ? {...settings} : null;
      if (updatedSettings) {
        if (updatedSettings.settlementTotalsDisplays?.length > 0) {
          const updatedDisplays = [...updatedSettings.settlementTotalsDisplays.filter(x => totalIds.some(z => z === x.id))];
          const newDisplays = totalIds.filter(x => !updatedDisplays.some(z => z.id === x)).map(x => ({
            id: x,
            isExpanded: false
          }));
          if (newDisplays.length > 0) {
            updatedDisplays.push(...newDisplays);
          }
          updatedSettings.settlementTotalsDisplays = updatedDisplays;
        } else {
          updatedSettings.settlementTotalsDisplays = totalIds.map(x => ({
            id: x,
            isExpanded: x === 'total'
          }));
        }
      }
      return updatedSettings;
    });
  }, []);

  const changeSettlementTotalDisplayHandler = useCallback((totalId: string) => {
    setUserSettings(settings => {
      const updatedSettings = settings ? {...settings} : null;
      if (updatedSettings) {
        const updatedDisplays = [...updatedSettings.settlementTotalsDisplays];
        const index = updatedDisplays.findIndex(x => x.id === totalId);
        let item = {...updatedDisplays[index]};
        item.isExpanded = !item.isExpanded;
        updatedDisplays[index] = item;
        updatedSettings.settlementTotalsDisplays = updatedDisplays;
      }
      return updatedSettings;
    });
  }, []);

  const changeTimesheetTotalsDisplaysHandler = useCallback((totalIds: string[]) => {
    setUserSettings(settings => {
      const updatedSettings = settings ? {...settings} : null;
      if (updatedSettings) {
        if (updatedSettings.timesheetTotalsDisplays?.length > 0) {
          const updatedDisplays = [...updatedSettings.timesheetTotalsDisplays.filter(x => totalIds.some(z => z === x.id))];
          const newDisplays = totalIds.filter(x => !updatedDisplays.some(z => z.id === x)).map(x => ({
            id: x,
            isExpanded: false
          }));
          if (newDisplays.length > 0) {
            updatedDisplays.push(...newDisplays);
          }
          updatedSettings.timesheetTotalsDisplays = updatedDisplays;
        } else {
          updatedSettings.timesheetTotalsDisplays = totalIds.map(x => ({
            id: x,
            isExpanded: x === 'total'
          }));
        }
      }
      return updatedSettings;
    });
  }, []);

  const changeTimesheetTotalDisplayHandler = useCallback((totalId: string) => {
    setUserSettings(settings => {
      const updatedSettings = settings ? {...settings} : null;
      if (updatedSettings) {
        const updatedDisplays = [...updatedSettings.timesheetTotalsDisplays];
        const index = updatedDisplays.findIndex(x => x.id === totalId);
        let item = {...updatedDisplays[index]};
        item.isExpanded = !item.isExpanded;
        updatedDisplays[index] = item;
        updatedSettings.timesheetTotalsDisplays = updatedDisplays;
      }
      return updatedSettings;
    });
  }, []);

  const replacePersistentUserSettingsHandler = useCallback(async (userSettings: IUserAppSettings) => {
    const storedInfoString = localStorage.getItem('userInfoRcbgDev');
    const storedInfo = {
      ...storedInfoString ? JSON.parse(storedInfoString) : null,
      userSettings
    };
    localStorage.setItem('userInfoRcbgDev', JSON.stringify(storedInfo));
    setUserSettings(userSettings);
  }, []);

  const savePersistentUserSettingsHandler = useCallback(async (): Promise<IResponseStatus | null> => {
    const storedInfoString = localStorage.getItem('userInfoRcbgDev');
    const storedInfo = {
      ...storedInfoString ? JSON.parse(storedInfoString) : null,
      userSettings
    };
    localStorage.setItem('userInfoRcbgDev', JSON.stringify(storedInfo));
    try {
      const callResponse: { status: IResponseStatus; } = await sendRequest(
        '/users/appSettings',
        'PATCH',
        {
          appSettings: userSettings
        },
        { Authorization: `Bearer ${token}` }
      );
      return callResponse.status;
    } catch (error) { return null; }
  }, [token, userSettings, sendRequest]);

  useEffect(() => {
    const storedInfoString = localStorage.getItem('userInfoRcbgDev');
    const storedInfo = {
      ...storedInfoString ? JSON.parse(storedInfoString) : null
    };
    let actualDate = new Date();
    actualDate.setHours(actualDate.getHours() + 3);
    if (storedInfo && storedInfo.token && new Date(storedInfo.expirationDate) > actualDate) {
      loginHandler(storedInfo.userId, storedInfo.userFirstName, storedInfo.userName, storedInfo.email, storedInfo.isAdmin, storedInfo.employeeId,
        storedInfo.masterDataPermissions, storedInfo.hierarchyPermissions, storedInfo.featurePermissions, storedInfo.userSettings,
        storedInfo.token, new Date(storedInfo.expirationDate));
    }
  }, [loginHandler]);

  useEffect(() => {
    if (token && expirationDate) {
      const timeToLive = expirationDate.getTime() - new Date().getTime();
      logoutTimerId = setTimeout(logoutHandler, timeToLive);
    } else {
      clearTimeout(logoutTimerId);
    }
  }, [token, expirationDate, logoutHandler])

  return (
    <UserContext.Provider
      value = {{
        isLoggedIn: !!token,
        userId,
        userFirstName,
        userName,
        email,
        isAdmin,
        employeeId,
        masterDataPermissions,
        hierarchyPermissions,
        featurePermissions,
        userSettings,
        token,
        expirationDate,
        logoutData,
        login: loginHandler,
        logout: logoutHandler,
        replacePersistentUserSettings: replacePersistentUserSettingsHandler,
        savePersistentUserSettings: savePersistentUserSettingsHandler,
        setBusinessArea: changeBusinessAreaHandler,
        setOutlet: changeOutletHandler,
        setDate: changeDateHandler,
        setJobBucketsFilter: changeJobBucketsFilterHandler,
        setLogoutData: changeLogoutDataHandler, 
        setPeriod: changePeriodHandler,
        setPreshiftTotalsDisplays: changePreshiftTotalsDisplaysHandler,
        setPreshiftTotalDisplay: changePreshiftTotalDisplayHandler,
        setSettlementTotalsDisplays: changeSettlementTotalsDisplaysHandler,
        setSettlementTotalDisplay: changeSettlementTotalDisplayHandler,
        setTimesheetTotalsDisplays: changeTimesheetTotalsDisplaysHandler,
        setTimesheetTotalDisplay: changeTimesheetTotalDisplayHandler,
        toggleDarkMode: toggleDarkModeHandler,
        togglePresentationMode: togglePresentationModeHandler
      }}>
        {props.children}
    </UserContext.Provider>
  );
};

export default UserContext;